import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';

/**
 * AuthRequiredStub — what unauthenticated visitors (and Googlebot) see
 * when they hit a protected route.
 *
 * The critical SEO behavior:
 *   • Emits <meta name="robots" content="noindex,nofollow"> via Helmet
 *     BEFORE any redirect runs. Google reads the meta on the rendered HTML
 *     and skips indexing — eliminating the "Page with redirect" diagnostic
 *     in Search Console for every auth-gated route.
 *   • Real users see a brief login prompt, then auto-redirect to the
 *     destination (default /) after 1.2s. Snappy enough to feel like the
 *     old <Navigate> behavior, slow enough that Helmet has time to mount.
 *   • A login button is rendered for users who'd rather click than wait.
 */
const AuthRequiredStub = ({ redirectTo = '/', message }) => {
  const navigate = useNavigate();
  useEffect(() => {
    const t = setTimeout(() => navigate(redirectTo, { replace: true }), 1200);
    return () => clearTimeout(t);
  }, [navigate, redirectTo]);

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex,nofollow" />
        <meta name="googlebot" content="noindex,nofollow" />
        <title>Sign in required — TurfPulse</title>
      </Helmet>
      <div
        className="min-h-screen flex items-center justify-center bg-zinc-50 px-6"
        data-testid="auth-required-stub"
      >
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold text-zinc-900">
            {message || 'Please sign in to continue'}
          </h1>
          <p className="mt-3 text-sm text-zinc-600">
            This page requires an account. Redirecting you home in a moment…
          </p>
          <button
            onClick={() => navigate(redirectTo, { replace: true })}
            className="mt-6 rounded-md bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800"
            data-testid="auth-required-redirect-now-btn"
          >
            Take me home
          </button>
        </div>
      </div>
    </>
  );
};

const LoadingSpinner = () => (
  <div data-testid="loading-spinner" className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

export const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user || user === false) return <AuthRequiredStub />;
  return children;
};

export const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user || user === false || user.role !== 'admin') {
    return <AuthRequiredStub message="Admin access required" />;
  }
  return children;
};

// Gated route for Pro / Business tier features (Property Measure, Quote PDFs).
// Admins also pass. Anyone else is bounced to the /pro upsell page.
export const ProTierRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user || user === false) return <AuthRequiredStub />;
  const isPro = user?.role === 'admin' || user?.subscription_tier === 'pro_business';
  if (!isPro) {
    return (
      <AuthRequiredStub
        redirectTo="/pro"
        message="Pro tier required for this feature"
      />
    );
  }
  return children;
};
